// ═══════════════════════════════════════════════════════════════════════════════
// CallStagePanel — AI Calling integration for ABM Journey stages 6 & 12
//
// Appears inside stages 6 (Alignment Call #1) and 12 (Confirmation Call #2)
// with full journey context pre-loaded. Allows MDA to:
//   1. Launch an AI-assisted or manual call directly from the journey stage
//   2. Monitor a live AI call with real-time transcript
//   3. After the call — auto-fill stage form fields from the call transcript
//   4. View call history for this contact
// ═══════════════════════════════════════════════════════════════════════════════
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Phone, PhoneCall, PhoneOff, Mic, Clock, Loader2,
  CheckCircle, AlertTriangle, ExternalLink, Volume2,
  FileText, Zap, ChevronDown, ChevronUp, Radio,
  Copy, Check, History, Settings,
} from 'lucide-react';
import apiClient from '@/services/api/apiClient';
import CallProgressStepper from '@/components/common/CallProgressStepper';

// ── Types ─────────────────────────────────────────────────────────────────────
interface CallRecord {
  id: string;
  provider: string;
  callType: string;
  status: string;
  duration?: number;
  transcript?: string;
  summary?: string;
  recordingUrl?: string;
  createdAt: string;
}

interface LiveCallStatus {
  callId: string;
  provider: string;
  status: string;
  duration: number | null;
  transcript: string | null;
  summary: string | null;
  recordingUrl: string | null;
  endReason: string | null;
}

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS_CFG: Record<string, { label: string; color: string; dot: string }> = {
  initiated:    { label: 'Initiating…', color: 'text-blue-600', dot: 'bg-blue-500 animate-pulse' },
  queued:       { label: 'Queued',      color: 'text-amber-600', dot: 'bg-amber-400 animate-pulse' },
  ringing:      { label: 'Ringing',    color: 'text-amber-600', dot: 'bg-amber-500 animate-pulse' },
  registered:   { label: 'Connecting', color: 'text-amber-600', dot: 'bg-amber-500 animate-pulse' },
  'in-progress':{ label: 'Live',       color: 'text-emerald-600', dot: 'bg-emerald-500 animate-pulse' },
  ongoing:      { label: 'Live',       color: 'text-emerald-600', dot: 'bg-emerald-500 animate-pulse' },
  ended:        { label: 'Ended',      color: 'text-muted-foreground', dot: 'bg-gray-400' },
  failed:       { label: 'Failed',     color: 'text-red-600', dot: 'bg-red-500' },
  error:        { label: 'Error',      color: 'text-red-600', dot: 'bg-red-500' },
};

function CopyBtn({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      className="flex items-center gap-1 text-xs px-2 py-0.5 rounded border border-border hover:bg-muted transition-colors"
    >
      {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

// ── Live call monitor ─────────────────────────────────────────────────────────
function LiveMonitor({
  callId, provider, onEnded,
}: { callId: string; provider: string; onEnded: (s: LiveCallStatus) => void }) {
  const [status, setStatus] = useState<LiveCallStatus | null>(null);
  const [polling, setPolling] = useState(true);
  const interval = useRef<any>(null);

  const poll = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/api/ai-calling/status/${callId}?provider=${provider}`);
      setStatus(data);
      if (['ended', 'error', 'failed'].includes(data.status)) {
        setPolling(false);
        clearInterval(interval.current);
        onEnded(data);
      }
    } catch { /* non-fatal */ }
  }, [callId, provider, onEnded]);

  useEffect(() => {
    poll();
    interval.current = setInterval(poll, 4000);
    return () => clearInterval(interval.current);
  }, [poll]);

  const endCall = async () => {
    try {
      await apiClient.post(`/api/ai-calling/end/${callId}`, { provider });
      setPolling(false);
      clearInterval(interval.current);
    } catch { /* non-fatal */ }
  };

  const s = status?.status || 'initiated';
  const cfg = STATUS_CFG[s] || STATUS_CFG.initiated;
  const isLive = ['in-progress', 'ongoing', 'ringing', 'registered', 'initiated'].includes(s);

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
      {/* Status row */}
      <div className="flex items-center justify-between">
        <div className={`flex items-center gap-2 ${cfg.color}`}>
          <span className={`h-2.5 w-2.5 rounded-full ${cfg.dot}`} />
          <Radio className="h-4 w-4" />
          <span className="text-sm font-medium">{cfg.label}</span>
          {status?.duration != null && (
            <span className="text-xs text-muted-foreground">· {Math.floor(status.duration / 60)}m {status.duration % 60}s</span>
          )}
        </div>
        {isLive && polling && (
          <button onClick={endCall}
            className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-600 transition-colors">
            <PhoneOff className="h-3 w-3" /> End Call
          </button>
        )}
      </div>

      {/* Progress stepper — what's happening right now */}
      <CallProgressStepper status={s} />

      {/* Live transcript */}
      {status?.transcript && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
              <Mic className="h-3 w-3" /> Live Transcript
            </p>
            <CopyBtn text={status.transcript} />
          </div>
          <div className="bg-background rounded-lg border border-border p-3 max-h-36 overflow-y-auto">
            <pre className="text-xs whitespace-pre-wrap font-sans leading-relaxed">{status.transcript}</pre>
          </div>
        </div>
      )}

      {/* Summary after call ends */}
      {status?.summary && (
        <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800">
          <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 mb-1">Call Summary</p>
          <p className="text-sm">{status.summary}</p>
        </div>
      )}

      {/* Recording */}
      {status?.recordingUrl && (
        <a href={status.recordingUrl} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-1.5 text-xs text-primary hover:underline">
          <Volume2 className="h-3 w-3" /> Listen to recording
        </a>
      )}

      {polling && (
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" /> Monitoring in real-time…
        </p>
      )}
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────
interface Props {
  stage: 6 | 12;
  journeyId: string;
  contactId: string;
  contactName?: string;
  accountName?: string;
  onTranscriptReady?: (transcript: string, summary?: string) => void; // auto-fill form fields
}

const CALL_TYPE = { 6: 'CALL1', 12: 'CALL2' } as const;
const STAGE_LABEL = { 6: 'Alignment Call #1', 12: 'Confirmation Call #2' };

export default function CallStagePanel({ stage, journeyId, contactId, contactName, accountName, onTranscriptReady }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [providers, setProviders] = useState<Record<string, any>>({});
  const [selectedProvider, setSelectedProvider] = useState<'vapi' | 'retell'>('vapi');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [agentNote, setAgentNote] = useState('');
  const [initiating, setInitiating] = useState(false);
  const [activeCall, setActiveCall] = useState<{ callId: string; provider: string } | null>(null);
  const [callHistory, setCallHistory] = useState<CallRecord[]>([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [lastEndedCall, setLastEndedCall] = useState<LiveCallStatus | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [expandedCalls, setExpandedCalls] = useState<Set<string>>(new Set());
  const toggleTranscript = (id: string) => setExpandedCalls(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');

  // Load providers and call history
  useEffect(() => {
    if (!expanded) return;
    apiClient.get('/api/ai-calling/providers').then(r => {
      setProviders(r.data);
      if (r.data.vapi?.configured) setSelectedProvider('vapi');
      else if (r.data.retell?.configured) setSelectedProvider('retell');
    }).catch(() => {});

    if (!historyLoaded) {
      apiClient.get(`/api/ai-calling/history/${contactId}`).then(r => {
        setCallHistory(r.data?.data || []);
        setHistoryLoaded(true);
      }).catch(() => { setHistoryLoaded(true); });
    }
  }, [expanded, contactId, historyLoaded]);

  const hasProvider = Object.values(providers).some((p: any) => p.configured);
  const recentCalls = callHistory.filter(c => c.callType === CALL_TYPE[stage]);

  const initiateCall = async () => {
    setError('');
    if (!hasProvider) {
      setError('No voice provider configured. Go to Settings → Integrations to add your Vapi or Retell key.');
      return;
    }
    setInitiating(true);
    try {
      const { data } = await apiClient.post('/api/ai-calling/initiate', {
        contactId,
        journeyId,
        provider: selectedProvider,
        callType: CALL_TYPE[stage],
        phoneNumber: phoneNumber || undefined,
        agentNote: agentNote || undefined,
      }, { timeout: 180000 });
      setActiveCall({ callId: data.callId, provider: selectedProvider });
      setLastEndedCall(null);
    } catch (e: any) {
      const d = e.response?.data;
      setError(d?.details || d?.error || e.message);
    } finally {
      setInitiating(false);
    }
  };

  const handleCallEnded = useCallback((status: LiveCallStatus) => {
    setLastEndedCall(status);
    setActiveCall(null);
    setHistoryLoaded(false); // refresh history

    // Auto-notify parent with transcript for form fill
    if (status.transcript && status.transcript.length > 50) {
      onTranscriptReady?.(status.transcript, status.summary || undefined);
    }
  }, [onTranscriptReady]);

  const PROVIDER_LABELS: Record<string, string> = { vapi: 'Vapi.ai', retell: 'Retell AI' };
  const STATUS_COLORS: Record<string, string> = {
    ended: 'text-muted-foreground', failed: 'text-red-500', 'in-progress': 'text-emerald-500',
    initiated: 'text-blue-500', ringing: 'text-amber-500',
  };

  // Manual "Sync" — pull the latest call result from the provider. Covers a missed
  // webhook, a UI timeout, or a refresh that lost the live poller. Uses the active call
  // if present, otherwise the most recent call on record for this contact. The backend
  // reconcile is idempotent, so this is always safe to click.
  const syncLatestCall = useCallback(async () => {
    setSyncing(true); setSyncMsg(''); setError('');
    try {
      const { data } = await apiClient.post(`/api/ai-calling/sync-contact/${contactId}`, {
        journeyId: journeyId || undefined,
        callType: CALL_TYPE[stage as keyof typeof CALL_TYPE] || 'CALL1',
      });
      if (data.found === false) {
        setSyncMsg(data.message || 'No call found to sync for this contact.');
      } else if (data.error) {
        setError(data.error);
      } else if (data.status === 'ended' && data.transcript) {
        setLastEndedCall({
          callId: data.callId, provider: data.provider || selectedProvider, status: 'ended',
          duration: data.duration, transcript: data.transcript, summary: data.summary,
          recordingUrl: data.recordingUrl, endReason: null,
        });
        setActiveCall(null);
        setSyncMsg(data.imported ? 'Imported the latest call from the provider.' : 'Latest call synced.');
        if (data.transcript.length > 50) onTranscriptReady?.(data.transcript, data.summary || undefined);
      } else {
        setSyncMsg(`Call status: ${data.status || 'unknown'}.`);
      }
      setHistoryLoaded(false);
    } catch (e: any) {
      setError(e?.response?.data?.details || e?.response?.data?.error || e.message);
    } finally {
      setSyncing(false);
    }
  }, [contactId, journeyId, stage, selectedProvider, onTranscriptReady]);

  return (
    <div className={`rounded-xl border-2 overflow-hidden transition-all ${
      expanded ? 'border-primary/30 bg-primary/3' : 'border-dashed border-primary/40 hover:border-primary/60'
    }`}>
      {/* Collapsed header */}
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-primary/5 transition-colors"
      >
        <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${
          recentCalls.length > 0 ? 'bg-emerald-100 dark:bg-emerald-950/40' : 'bg-primary/10'
        }`}>
          {recentCalls.length > 0
            ? <CheckCircle className="h-5 w-5 text-emerald-500" />
            : <Phone className="h-5 w-5 text-primary" />
          }
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-primary">{STAGE_LABEL[stage]}</p>
            {recentCalls.length > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 font-medium">
                {recentCalls.length} call{recentCalls.length !== 1 ? 's' : ''} logged
              </span>
            )}
            {activeCall && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500 text-white animate-pulse font-medium">
                LIVE
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {recentCalls.length > 0
              ? `Last: ${new Date(recentCalls[0].createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`
              : 'Launch AI voice call or log manual call notes'}
          </p>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
      </button>

      {/* Expanded panel */}
      {expanded && (
        <div className="border-t border-primary/20 p-4 space-y-4">

          {/* Provider + phone row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Voice Provider</label>
              <div className="flex gap-2">
                {['vapi', 'retell'].map(p => (
                  <button key={p}
                    onClick={() => setSelectedProvider(p as 'vapi' | 'retell')}
                    disabled={!providers[p]?.configured}
                    className={`flex-1 text-xs px-2 py-1.5 rounded-lg border transition-all ${
                      selectedProvider === p && providers[p]?.configured
                        ? 'border-primary bg-primary/10 text-primary font-medium'
                        : providers[p]?.configured
                          ? 'border-border hover:border-primary/40'
                          : 'border-dashed border-border opacity-50 cursor-not-allowed'
                    }`}
                  >
                    {PROVIDER_LABELS[p]}
                    {providers[p]?.configured && (
                      <CheckCircle className="h-3 w-3 text-emerald-500 inline ml-1" />
                    )}
                  </button>
                ))}
              </div>
              {!hasProvider && (
                <a href="/settings" className="text-xs text-primary hover:underline flex items-center gap-1 mt-1">
                  <Settings className="h-3 w-3" /> Configure in Settings
                </a>
              )}
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Phone Number</label>
              <input
                value={phoneNumber}
                onChange={e => setPhoneNumber(e.target.value)}
                placeholder="+91 98765 43210"
                className="w-full text-sm px-2.5 py-1.5 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
          </div>

          {/* Agent note */}
          <div>
            <label className="text-xs text-muted-foreground block mb-1">
              Coaching note for AI agent <span className="text-muted-foreground font-normal">(optional)</span>
            </label>
            <input
              value={agentNote}
              onChange={e => setAgentNote(e.target.value)}
              placeholder={stage === 6
                ? "e.g. 'They confirmed EXECUTION pain, mentioned SAP + Wonderware stack'"
                : "e.g. 'CFO Mehta needs to be on this call, architecture direction confirmed'"}
              className="w-full text-sm px-2.5 py-1.5 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          {/* What the AI will do */}
          <div className="rounded-lg bg-primary/5 border border-primary/20 p-3">
            <p className="text-xs font-semibold text-primary mb-2 flex items-center gap-1.5">
              <Zap className="h-3.5 w-3.5" /> AI Agent Context for {STAGE_LABEL[stage]}
            </p>
            <ul className="space-y-1 text-xs text-foreground">
              {stage === 6 ? [
                'Opens referencing the specific pain axis and maturity gap established in stages 1–5',
                'Validates: SAP-shopfloor gap, OT stack, data readiness, automation priority',
                'Probes: who else is involved, what happened with previous attempts, budget signals',
                'Closes with: architecture diagram walkthrough scheduled for next call',
              ] : [
                'Opens by referencing all confirmed facts from Alignment Call #1 and stages 7–11',
                'Validates: architecture direction, buying group composition, decision process',
                'Confirms: timeline, budget authority, technical champion',
                'Closes with: MQL qualification criteria and sales handoff prep',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-primary font-bold shrink-0">{i + 1}.</span>{item}
                </li>
              ))}
            </ul>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800">
              <AlertTriangle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
              <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Launch button */}
          {!activeCall && (
            <button
              onClick={initiateCall}
              disabled={initiating || !hasProvider}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {initiating
                ? <><Loader2 className="h-4 w-4 animate-spin" /> Initiating call…</>
                : <><PhoneCall className="h-4 w-4" /> Launch AI {STAGE_LABEL[stage]} via {PROVIDER_LABELS[selectedProvider]}</>
              }
            </button>
          )}

          {/* Initiating — showcase what's happening before the call is live */}
          {initiating && !activeCall && (
            <div className="rounded-lg border border-border p-4">
              <CallProgressStepper initiating />
            </div>
          )}

          {/* Manual sync — fetch the latest call result from the provider */}
          {!activeCall && (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={syncLatestCall}
                disabled={syncing}
                title="Fetch the latest call transcript from the provider. Use this if the result didn't appear, or after a page refresh."
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-border hover:bg-muted disabled:opacity-50 transition-colors"
              >
                {syncing
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Syncing…</>
                  : <><History className="h-3.5 w-3.5" /> Sync latest call</>}
              </button>
              {syncMsg && <span className="text-xs text-muted-foreground">{syncMsg}</span>}
            </div>
          )}

          {/* Live call monitor */}
          {activeCall && (
            <LiveMonitor
              callId={activeCall.callId}
              provider={activeCall.provider}
              onEnded={handleCallEnded}
            />
          )}

          {/* Post-call result */}
          {lastEndedCall && !activeCall && (
            <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/20 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500" />
                <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">Call complete</p>
                {lastEndedCall.duration && (
                  <span className="text-xs text-muted-foreground ml-auto">
                    {Math.floor(lastEndedCall.duration / 60)}m {lastEndedCall.duration % 60}s
                  </span>
                )}
              </div>
              {lastEndedCall.summary && (
                <p className="text-sm">{lastEndedCall.summary}</p>
              )}
              {lastEndedCall.transcript && (
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-muted-foreground">Transcript</p>
                    <CopyBtn text={lastEndedCall.transcript} />
                  </div>
                  <pre className="text-xs whitespace-pre-wrap font-sans bg-background rounded border p-2 max-h-32 overflow-y-auto leading-relaxed">
                    {lastEndedCall.transcript}
                  </pre>
                </div>
              )}
              {onTranscriptReady && lastEndedCall.transcript && (
                <button
                  onClick={() => onTranscriptReady(lastEndedCall.transcript!, lastEndedCall.summary || undefined)}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  <Zap className="h-3 w-3" /> Auto-fill stage form from transcript
                </button>
              )}
              {lastEndedCall.recordingUrl && (
                <a href={lastEndedCall.recordingUrl} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-primary hover:underline">
                  <Volume2 className="h-3 w-3" /> Listen to recording
                </a>
              )}
            </div>
          )}

          {/* Call history for this stage */}
          {recentCalls.length > 0 && (
            <div>
              <button
                onClick={() => setShowHistory(v => !v)}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <History className="h-3.5 w-3.5" />
                {showHistory ? 'Hide' : 'Show'} {recentCalls.length} previous call{recentCalls.length !== 1 ? 's' : ''}
                {showHistory ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </button>
              {showHistory && (
                <div className="mt-2 space-y-2">
                  {recentCalls.map(c => {
                    const sc = STATUS_CFG[c.status] || STATUS_CFG.ended;
                    return (
                      <div key={c.id} className="rounded-lg border border-border p-3 space-y-1">
                        <div className="flex items-center gap-2 text-xs">
                          <span className={`h-2 w-2 rounded-full ${sc.dot}`} />
                          <span className={`font-medium ${sc.color}`}>{sc.label}</span>
                          <span className="text-muted-foreground">·</span>
                          <span className="text-muted-foreground capitalize">{c.provider}</span>
                          {c.duration && <span className="text-muted-foreground">· {Math.floor(c.duration / 60)}m {c.duration % 60}s</span>}
                          <span className="text-muted-foreground ml-auto">
                            {new Date(c.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        {c.transcript && (
                          <div className="pl-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => toggleTranscript(c.id)}
                                className="text-xs text-primary hover:underline flex items-center gap-1"
                              >
                                <FileText className="h-3 w-3" />
                                {expandedCalls.has(c.id) ? 'Hide' : 'View full'} transcript
                                {expandedCalls.has(c.id) ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                              </button>
                              {expandedCalls.has(c.id) && <CopyBtn text={c.transcript} />}
                            </div>
                            {expandedCalls.has(c.id) ? (
                              <pre className="mt-1.5 text-xs text-foreground whitespace-pre-wrap font-sans leading-relaxed max-h-96 overflow-y-auto bg-muted/40 rounded-lg p-3">{c.transcript}</pre>
                            ) : (
                              <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{c.transcript.slice(0, 150)}…</p>
                            )}
                          </div>
                        )}
                        {c.transcript && onTranscriptReady && (
                          <button
                            onClick={() => onTranscriptReady(c.transcript!, c.summary)}
                            className="ml-4 text-xs text-primary hover:underline flex items-center gap-1"
                          >
                            <Zap className="h-3 w-3" /> Use this transcript to fill form
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
